import React, { useState } from 'react';
import { Check, Crown, Sparkles } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog';
import { apiService } from 'services/api';
import { useSubscription } from 'hooks/useSubscription';
import { cn } from 'lib/utils';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PricingPlan {
  name: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  savings?: number;
  featured?: boolean;
}

const PRICING_PLANS: Record<'monthly' | 'annual', PricingPlan> = {
  monthly: {
    name: 'Monthly',
    price: 12,
    period: 'month',
    description: 'Perfect for trying out premium features',
    features: [
      'Unlimited AI health analysis',
      'Unlimited health assessments',
      'Unlimited nutrition tracking',
      'Unlimited health actions',
      'Advanced AI insights',
      'Priority support',
      'Multiple export formats',
    ],
    featured: false,
  },
  annual: {
    name: 'Annual',
    price: 99,
    period: 'year',
    description: 'Best value - save 31%',
    savings: 45,
    features: [
      'Everything in Monthly',
      '31% savings ($45/year)',
      'Priority AI responses',
      'Early access to features',
      'Annual commitment discount',
    ],
    featured: true,
  },
};

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getUserEmail } = useSubscription();

  const handleSubscribe = async (plan: 'monthly' | 'annual') => {
    setLoading(true);
    setError(null);

    try {
      const email = getUserEmail();
      const session = await apiService.createCheckoutSession(email, plan);
      window.location.href = session.url;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create checkout session';
      setError(errorMessage);
      console.error('Error creating checkout session:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        {loading && (
          <div className="absolute inset-0 bg-card/90 flex items-center justify-center rounded-lg z-10">
            <div>Redirecting to checkout...</div>
          </div>
        )}

        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-3">
            <Crown size={28} />
            Upgrade to Premium
          </DialogTitle>
          <DialogDescription>
            Unlock unlimited AI health analysis, assessments, and insights
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {Object.entries(PRICING_PLANS).map(([key, plan]) => (
            <div
              key={key}
              className={cn(
                'rounded-lg p-7 flex flex-col gap-5 relative border-2',
                plan.featured
                  ? 'bg-muted border-primary'
                  : 'bg-card border'
              )}
            >
              {plan.featured && (
                <div className="absolute -top-3 right-4 bg-primary text-primary-foreground px-2 py-0.5 rounded-full text-xs font-semibold">
                  Best Value
                </div>
              )}

              <div className="flex flex-col gap-2">
                <h3 className="m-0 text-lg font-bold text-foreground-strong flex items-center gap-2">
                  {plan.featured && <Sparkles size={20} />}
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-foreground-strong">${plan.price}</span>
                  <span className="text-base text-foreground-muted font-medium">/{plan.period}</span>
                </div>
                {plan.savings && (
                  <span className="text-base text-primary font-semibold">
                    Save ${plan.savings}/year
                  </span>
                )}
                <p className="m-0 text-foreground-muted text-sm leading-relaxed">{plan.description}</p>
              </div>

              <ul className="list-none p-0 m-0 flex flex-col gap-3 flex-1">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-foreground text-sm">
                    <Check size={20} className="text-primary shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant={plan.featured ? 'default' : 'outline'}
                className="mt-auto w-full py-3.5 font-semibold text-base h-auto"
                onClick={() => handleSubscribe(key as 'monthly' | 'annual')}
                disabled={loading}
              >
                {loading ? 'Processing...' : `Choose ${plan.name}`}
              </Button>
            </div>
          ))}
        </div>

        {error && (
          <div className="px-8 pb-4 text-destructive text-sm">
            {error}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
